/**
 * EL ADAPTADOR DEL CATÁLOGO.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ESTE ES EL ÚNICO ARCHIVO QUE CONOCE LA FORMA DEL API.                    ║
 * ║  El contrato lo está escribiendo otro agente. Cuando cierre, se ajusta     ║
 * ║  AQUÍ y en ningún otro sitio: `Marketplace.js` habla de `price`, `photos`  ║
 * ║  y `compatibleVehicles`, que son los nombres que la app ya usa.            ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * ─── LO QUE ESTE ARCHIVO EXISTE PARA ARREGLAR ───────────────────────────────
 *
 * Hoy el Marketplace trae 20 productos de Firestore y aplica los filtros EN EL
 * NAVEGADOR sobre esos 20. Filtrar "Toyota" no enseña los Toyota del catálogo:
 * enseña los Toyota que hubiera entre los 20 más recientes de toda la
 * plataforma. Lo mismo con el precio, la categoría, la ciudad y el orden. Es la
 * queja del dueño y no admite un arreglo a medias: los filtros van al servidor,
 * el servidor devuelve la página Y EL TOTAL, y el total es lo que permite decir
 * "página 1 de 7" en vez de un "cargar más" que nunca dice cuánto falta.
 *
 * ─── LO QUE EL ENDPOINT TIENE QUE HACER, Y QUE AQUÍ NO SE PUEDE COMPROBAR ───
 *
 * 🔴 SOLO TALLERES CON SUSCRIPCIÓN VIGENTE. Hoy lo aplica el navegador
 *    (`hasActiveSubscription` sobre `workshops`) y es una regla de NEGOCIO, no
 *    una preferencia del usuario: un taller que dejó de pagar no puede seguir
 *    vendiendo. Si el endpoint no la aplica, se filtra mercancía que no debería
 *    estar a la venta. `Marketplace.js` conserva una red de seguridad en el
 *    cliente y AVISA por consola si el API le manda algo que no debería.
 * 🔴 NADA `adminRestricted`, nada `active:false`, nada sin stock.
 * 🔴 La compatibilidad se resuelve con `IS NULL OR ...` (ver `COMPATIBILITY_SQL`
 *    en api/src/types/catalog.ts): traducir `yearFrom` a `year >= year_from` a
 *    secas deja fuera todas las filas con NULL y vacía el catálogo sin un error.
 */
import { apiFetch, ApiError } from './client';

/**
 * 50 por página. Lo pidió el dueño con estas palabras: "me gustaría que muestren
 * de 50 en 50". No es un número que se ajuste por comodidad de implementación.
 */
export const CATALOG_PAGE_SIZE = 50;

/**
 * Tope de profundidad del API (`MAX_PAGE` en api/src/services/catalog.ts). Pedir
 * la página 201 no es "sale vacía": es un 400, o sea la pantalla en rojo por
 * culpa nuestra. Se recorta aquí para que el paginador no ofrezca un botón que
 * lleva a un error.
 */
export const CATALOG_MAX_PAGE = 200;

/** Número o nada. Un `''` de un input vacío no es un cero. */
function num(v) {
  if (v === undefined || v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Texto con contenido, o nada. */
function txt(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? undefined : s;
}

/** Booleano solo cuando está encendido: `delivery=false` no es un filtro. */
function on(v) {
  return v === true ? true : undefined;
}

/** Pesos (lo que escribe el usuario) → centavos (lo que entiende el API). */
function cents(v) {
  const n = num(v);
  return n === undefined ? undefined : Math.round(n * 100);
}

/**
 * Los filtros de la pantalla, traducidos a parámetros del API.
 *
 * ⚠️ `ProductQuerySchema` es `.strict()`: un parámetro que el API no conozca no
 * se ignora, devuelve 400 — o sea la pantalla en rojo por culpa nuestra. Los
 * nombres de aquí abajo son los de `api/src/services/catalog.ts` y no se
 * inventan.
 *
 * El vehículo viaja DESARMADO (marca, modelo, año, tipo) a propósito: la
 * compatibilidad se resuelve en SQL contra `product_compatibility`, que es todo
 * el punto. Mandar un `vehicleId` obligaría al API a leer el vehículo del
 * cliente para nada.
 */
export function catalogQuery({
  page = 1,
  pageSize = CATALOG_PAGE_SIZE,
  search,
  category,
  workshopId,
  city,
  minPrice,
  maxPrice,
  onlyDelivery,
  onlyInstallation,
  sort,
  vehicle,
  lat,
  lng,
} = {}) {
  const p = Math.min(CATALOG_MAX_PAGE, Math.max(1, Math.floor(num(page) || 1)));
  const texto = txt(search);
  const coords = num(lat) !== undefined && num(lng) !== undefined;

  const q = {
    page: p,
    pageSize,
    q: texto,
    category: txt(category),
    workshopId: txt(workshopId),
    city: txt(city),
    // 🔴 CENTAVOS. La casilla del filtro está en pesos; mandar el número tal cual
    // pediría "de RD$5 para arriba" cuando el usuario escribió 500, y el filtro
    // dejaría de filtrar sin dar ni un error.
    minPriceCents: cents(minPrice),
    maxPriceCents: cents(maxPrice),
    delivery: on(onlyDelivery),
    installation: on(onlyInstallation),
  };

  // ─── El orden, con las dos reglas que el API impone ───────────────────────
  const orden = txt(sort);
  if (orden === 'nearest') {
    // `sort=nearest` sin lat/lng es un 400. Sin permiso de ubicación se deja
    // decidir al servidor en vez de romper la pantalla.
    if (coords) q.sort = 'nearest';
  } else if (orden === 'recent' && texto) {
    // Se OMITE a propósito: sin `sort`, el API ordena por relevancia cuando hay
    // texto. 'recent' es el valor por defecto del desplegable, no una elección;
    // mandarlo a la fuerza haría que buscar "freno" devolviera lo más nuevo en
    // vez de lo que mejor coincide.
  } else if (orden) {
    q.sort = orden;
  }

  if (vehicle) {
    const marca = txt(vehicle.brand ?? vehicle.make);
    if (marca) {
      // `model`/`year` sin `make` es un 400. Van juntos o no van.
      q.make = marca;
      q.model = txt(vehicle.model);
      q.year = num(vehicle.year);
    }
    // Mismo criterio que `isCompatible` en Marketplace.js: cualquier cosa que no
    // sea 'moto' es 'car'. El API lo espera en mayúsculas.
    q.vehicleCategory = vehicle.vehicleType === 'moto' ? 'MOTO' : 'CAR';
  }

  // "Más cercano" necesita desde dónde. No se manda medio par de coordenadas.
  if (coords) {
    q.lat = num(lat);
    q.lng = num(lng);
  }

  for (const k of Object.keys(q)) if (q[k] === undefined) delete q[k];
  return q;
}

/**
 * Centavos → pesos. `null` se conserva como `null`.
 *
 * 🔴 Un precio ausente NO es cero. `Number(p.price) || 0` es exactamente el bug
 * que `CORRUPT_PRICE_THROWS_INSTEAD_OF_FREE` documenta en el contrato: convertía
 * `undefined` en una línea GRATIS y la orden se creaba igual.
 */
function pesos(cents) {
  if (cents === undefined || cents === null) return null;
  const n = Number(cents);
  return Number.isFinite(n) ? n / 100 : null;
}

/** El valor en pesos, aceptando que el API mande centavos o (aún) pesos. */
function money(cents, legacy) {
  const v = pesos(cents);
  if (v !== null) return v;
  if (legacy === undefined || legacy === null) return null;
  const n = Number(legacy);
  return Number.isFinite(n) ? n : null;
}

/** Las fotos, vengan como texto suelto o como filas con `url`. */
function fotos(row) {
  const raw = row.photos ?? row.images ?? [];
  if (!Array.isArray(raw)) return [];
  return raw.map((f) => (typeof f === 'string' ? f : f && (f.url || f.photoUrl))).filter(Boolean);
}

/**
 * Una fila del API con los nombres que la app ya usa.
 *
 * 🔴 EL `id` DE UNA VARIACIÓN ES `variationId`, NO el `id` de la fila. El
 * contrato lleva los dos: `id` es la clave de la tabla y `variationId` es el
 * `Date.now().toString()` heredado que apuntan los carritos vivos en
 * `localStorage` de dispositivos que nadie controla y las órdenes históricas
 * (`VARIATION_ID_IS_A_TIMESTAMP_STRING`). Coger el equivocado envenena carritos
 * y el cliente no puede completar la compra sin que nada en pantalla lo explique.
 */
export function adaptProduct(row) {
  if (!row || typeof row !== 'object') return null;
  const compat = row.compatibility ?? row.compatibleVehicles ?? [];

  // El LISTADO manda `variationCount` (cuántas hay); el DETALLE manda las
  // variaciones enteras. `ProductCard` solo lee `variations.length` para su
  // insignia "N opciones" y para el botón "Seleccionar opción", así que con el
  // conteo se rellenan huecos EXPLÍCITAMENTE VACÍOS: el número es dato real, los
  // precios no se inventan. Quien necesite el precio de una variación va al
  // detalle, que es donde vive.
  const variaciones = Array.isArray(row.variations)
    ? row.variations.map((v) => ({
        id: v.variationId ?? v.id,          // 🔴 ver el recuadro de arriba
        name: v.name,
        price: money(v.priceCents, v.price),
        stock: v.stock ?? 0,
      }))
    : Array.from({ length: Number(row.variationCount) || 0 }, () => ({
        id: null, name: null, price: null, stock: null,
      }));

  return {
    id: row.id,
    workshopId: row.workshopId ?? null,
    workshopName: row.workshopName ?? null,
    workshopCity: row.workshopCity ?? null,
    workshopRating: row.workshopRating ?? null,
    name: row.name ?? '',
    description: row.description ?? null,
    category: row.category ?? null,
    // ⚠️ LA MISMA MARCA CON DOS NOMBRES. En Firestore el campo es `brand` (lo
    // escribe `WorkshopProducts`); en Postgres es `part_brand` → `partBrand`.
    // Se publican los dos apuntando al mismo valor para que ninguna pantalla
    // tenga que saber de qué base vino: sin este puente, la línea "Marca: Bosch"
    // de la ficha se queda en blanco y no hay ningún error que lo delate.
    brand: row.brand ?? row.partBrand ?? null,
    partBrand: row.partBrand ?? row.brand ?? null,
    partNumber: row.partNumber ?? null,

    price: money(row.priceCents, row.price),
    comparePrice: money(row.comparePriceCents, row.comparePrice),
    supplierPrice: money(row.supplierPriceCents, row.supplierPrice),
    installationPrice: money(row.installationPriceCents, row.installationPrice),
    sellsToDistributors: row.sellsToDistributors === true,

    photos: fotos(row),
    videoURL: row.videoUrl ?? row.videoURL ?? null,
    stock: row.stock ?? null,
    inStock: row.inStock !== false,
    active: row.active !== false,
    adminRestricted: row.adminRestricted === true,

    // El API dice 'CAR'/'MOTO'; toda la app compara con 'car'/'moto'/'both'.
    // Sin bajarlo a minúsculas, la insignia de compatibilidad de `ProductCard`
    // no sale NUNCA y nadie ve un error en ningún sitio.
    vehicleCategory: (row.vehicleCategory ?? 'car').toString().toLowerCase(),
    isUniversal: row.isUniversal === true,
    deliveryAvailable: row.deliveryAvailable === true,
    pickupAvailable: row.pickupAvailable === true,
    installationAvailable: row.installationAvailable === true,

    hasVariations: row.hasVariations === true,
    variationLabel: row.variationLabel ?? null,
    variationCount: Number(row.variationCount) || variaciones.length,
    variations: variaciones,

    // NULL = sin límite. No convertir a 0 ni a ''.
    compatibleVehicles: Array.isArray(compat)
      ? compat.map((c) => ({
          make: c.make ?? null,
          model: c.model ?? null,
          yearFrom: c.yearFrom ?? null,
          yearTo: c.yearTo ?? null,
        }))
      : [],

    createdAt: row.createdAt ?? null,
  };
}

/**
 * ─── EL VEREDICTO DE LA BÚSQUEDA ────────────────────────────────────────────
 *
 * 🔴 LO QUE PASA SI ESTOS TRES CAMPOS SE QUEDAN EN EL CABLE. El API los calcula
 * y hasta hoy este adaptador los tiraba, así que la pantalla no podía distinguir
 * dos cosas que no se parecen en nada:
 *
 *  · `matchMode: 'fuzzy'` significa que la coincidencia exacta dio CERO y lo que
 *    se está enseñando sale del respaldo por errata. Pintarlo como si fuera lo
 *    pedido es como un buscador pierde la confianza: el cliente cree que eso es
 *    lo que hay.
 *  · `whyEmpty` es la diferencia entre «no vendemos pastillas de freno» y «sí las
 *    vendemos, pero el filtro de TU carro las quitó». El servidor ya pagó las
 *    consultas para saberlo; tirarlo aquí convierte la respuesta útil en el
 *    estado vacío genérico, que es exactamente la lectura cara.
 *  · `interpreted` dice si el API decidió por su cuenta que una palabra era la
 *    MARCA de un carro. Una reinterpretación silenciosa desconcierta.
 *
 * Se normalizan a la defensiva: un contrato que aún no los mande deja
 * `matchMode: null` y `whyEmpty: null`, y la pantalla se comporta como antes.
 */
const MATCH_MODES = ['exact', 'fuzzy', 'none'];

/** Entero o 0. Un conteo que no llega no es un conteo negativo ni un NaN. */
function conteo(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Cómo entendió el API lo que se tecleó. Siempre un objeto, nunca `undefined`. */
function adaptInterpretation(raw) {
  const makes = Array.isArray(raw?.makes) ? raw.makes.filter((m) => typeof m === 'string' && m) : [];
  const text = typeof raw?.text === 'string' && raw.text ? raw.text : null;
  return { makes, text };
}

/** El diagnóstico del vacío. `null` cuando no hay nada que explicar. */
function adaptWhyEmpty(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    withoutFilters: conteo(raw.withoutFilters),
    withoutQuery: conteo(raw.withoutQuery),
    terms: Array.isArray(raw.terms)
      ? raw.terms.filter((t) => t && typeof t.term === 'string').map((t) => ({ term: t.term, total: conteo(t.total) }))
      : [],
    filters: Array.isArray(raw.filters)
      ? raw.filters.filter((f) => f && typeof f.filter === 'string').map((f) => ({ filter: f.filter, total: conteo(f.total) }))
      : [],
  };
}

/**
 * LO ÚNICO ÚTIL QUE SE PUEDE DECIR CUANDO NO SALIÓ NADA, en un solo dato.
 *
 * El orden NO es arbitrario, va de lo más accionable a lo menos:
 *
 *  1. `filter` — UN filtro concreto que, quitándolo, deja resultados. Es lo
 *     único que el cliente arregla con un toque, y es el caso caro: entra con su
 *     carro puesto, busca «pastillas de freno», y se va creyendo que no las
 *     vendemos porque ningún vendedor llenó la compatibilidad de SU modelo.
 *  2. `filters` — hacen falta quitar varios; ninguno solo alcanza.
 *  3. `term` — la Y entre palabras es la que mata: una sola de ellas sí
 *     encuentra. «pastillas freno toyota» con "toyota" que no está en ningún
 *     lado.
 *  4. `query` — con estos filtros SÍ hay mercancía; lo que no encuentra es el
 *     texto.
 *
 * `null` cuando de verdad no hay nada honesto que decir, y entonces el estado
 * vacío de siempre es la respuesta correcta: no lo tenemos.
 */
export function searchEmptyReason(page) {
  const d = page && page.whyEmpty;
  if (!d) return null;

  const mayor = (lista) => lista.filter((x) => x.total > 0).sort((a, b) => b.total - a.total)[0] || null;

  const filtro = mayor(d.filters);
  if (filtro) return { kind: 'filter', filter: filtro.filter, total: filtro.total };
  if (d.withoutFilters > 0) return { kind: 'filters', total: d.withoutFilters };

  const palabra = mayor(d.terms);
  if (palabra) return { kind: 'term', term: palabra.term, total: palabra.total };
  if (d.withoutQuery > 0) return { kind: 'query', total: d.withoutQuery };

  return null;
}

/**
 * La respuesta del API convertida en lo que la pantalla necesita.
 *
 * `total` puede faltar (un contrato que aún no lo mande): entonces `totalPages`
 * es `null` y la pantalla lo dice como puede, en vez de fingir un "de 7" que se
 * inventó. Mentir sobre cuántas páginas hay es la misma clase de mentira que la
 * que estamos arreglando.
 */
export function adaptCatalogPage(payload, { page = 1, pageSize = CATALOG_PAGE_SIZE } = {}) {
  const lista = payload && (payload.items ?? payload.products ?? payload.data);
  if (!Array.isArray(lista)) {
    throw new ApiError('El API no devolvió una lista de productos.', { code: 'bad_response' });
  }

  const totalCrudo = payload.total ?? payload.totalCount ?? payload.count;
  const total = Number.isFinite(Number(totalCrudo)) && totalCrudo !== null && totalCrudo !== undefined
    ? Number(totalCrudo)
    : null;

  const tam = Number(payload.pageSize) || pageSize;
  // El servidor ya lo calcula. Se prefiere el suyo para que no haya dos cuentas
  // distintas del mismo número; el `ceil` es el respaldo.
  const paginasDelServidor = Number(payload.totalPages);

  const pagina = {
    products: lista.map(adaptProduct).filter(Boolean),
    total,
    // Sin resultados sigue habiendo UNA página: no existe "página 1 de 0".
    totalPages: Number.isFinite(paginasDelServidor) && paginasDelServidor > 0
      ? paginasDelServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload.page) || page,
    pageSize: tam,
    // El veredicto de la búsqueda. Ver el recuadro de arriba: sin estos tres, un
    // resultado aproximado se enseña como exacto y un vacío explicable se enseña
    // como "no tenemos esa pieza".
    matchMode: MATCH_MODES.includes(payload.matchMode) ? payload.matchMode : null,
    interpreted: adaptInterpretation(payload.interpreted),
    whyEmpty: adaptWhyEmpty(payload.whyEmpty),
  };

  // El motivo se resuelve AQUÍ y no en la pantalla, aunque la pantalla sea la
  // única que lo pinta: este archivo es el que conoce la forma del contrato, y
  // repartir su lectura es la deuda que la migración está pagando. De paso, la
  // pantalla no tiene que importar nada nuevo para entender su propia respuesta.
  pagina.emptyReason = searchEmptyReason(pagina);
  return pagina;
}

/**
 * Una página del catálogo.
 *
 * `apiFetchImpl` se inyecta en pruebas. Los errores SUBEN: quien llama tiene que
 * poder distinguir "falló" de "no hay productos", que es la diferencia entre
 * `LoadFailed` y el estado vacío.
 */
export async function fetchCatalogPage(filtros = {}, { apiFetchImpl, signal } = {}) {
  const query = catalogQuery(filtros);
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/products', { query, signal });
  return adaptCatalogPage(payload, { page: query.page, pageSize: query.pageSize });
}

/**
 * LA TIENDA DE UN NEGOCIO — la única parte del catálogo que abre SIN SESIÓN.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 NO ES `fetchCatalogPage({ workshopId })`. ES OTRA PUERTA.               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * La ficha pública del taller (`/workshop/profile/:id`) la abre cualquiera —es
 * el enlace que el negocio comparte por WhatsApp—, y `GET /products` exige
 * token. Medido en producción: al visitante sin cuenta la petición le devolvía
 * 401 y la pantalla lo pintaba como «este negocio no tiene productos», que es
 * exactamente el bloque que convence a alguien de entrar.
 *
 * La vitrina general NO se abrió y no debe abrirse por aquí: es el catálogo de
 * TODOS los talleres con filtros de servidor, orden y total, o sea la
 * herramienta que hace eficiente vaciarlo, y el API todavía no tiene limitador
 * por IP. Esta ruta cuelga de UN negocio: para pedirla hay que conocer su id.
 *
 * Devuelve lo MISMO que la vitrina (misma consulta en el servidor), así que la
 * pantalla no tiene que saber por cuál de las dos vino.
 */
export async function fetchWorkshopStorefront(
  workshopId,
  { page = 1, pageSize = CATALOG_PAGE_SIZE, apiFetchImpl, signal } = {},
) {
  const call = apiFetchImpl || apiFetch;
  // Sin filtros: es la tienda entera de ese negocio. El `workshopId` viaja en la
  // RUTA y mandarlo además en la consulta es un 400 que lo nombra.
  const payload = await call(`/workshops/${encodeURIComponent(workshopId)}/storefront`, {
    query: { page, pageSize },
    signal,
  });
  return adaptCatalogPage(payload, { page, pageSize });
}

/**
 * La FICHA de un producto. Vive aquí, con la lista, para que la pantalla de
 * detalle no tenga que escribirse su propio `fetch`: repartir llamadas por las
 * pantallas es la deuda que esta migración está pagando.
 *
 * ⚠️ EL 404 NO ES UN ERROR, ES UNA RESPUESTA. El API devuelve 404 tanto si el
 * producto no existe como si el admin lo restringió (distinguirlos le diría a
 * cualquiera qué productos están sancionados). "Este producto ya no está" es
 * información para el usuario; pintar la pantalla de rojo con "no se pudo
 * cargar" sería mentirle y hacerle reintentar para siempre. Cualquier OTRO
 * fallo sí sube.
 */
export async function fetchProduct(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    const row = await call(`/products/${encodeURIComponent(id)}`, { signal });
    return adaptProduct(row);
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

/**
 * ─── LAS RESEÑAS DEL PRODUCTO ───────────────────────────────────────────────
 *
 * Viven aquí, con la lista y con la ficha, y no en la pantalla. Es el mismo
 * motivo de siempre: tres copias del mismo lector hubo que unificar esta misma
 * semana, y una cuarta escrita dentro de `ProductDetail.js` sería exactamente la
 * deuda que esta migración está pagando.
 *
 * 🔴 EL PROMEDIO ES EL DE TODAS, Y ESO CAMBIA UN NÚMERO QUE EL CLIENTE VE.
 *
 * Hoy la pantalla trae las 20 reseñas más recientes, promedia ESAS 20 y enseña
 * el resultado como si fuera la nota del producto: un artículo con 200 reseñas
 * muestra la nota de las últimas 20 rotulada "(200)". El endpoint devuelve
 * `total` y `average` calculados sobre TODAS, en el MISMO viaje, y son los que
 * se usan por este camino. El día que el interruptor se encienda, la nota de
 * algunos productos va a MOVERSE, y puede BAJAR. Es más cierta; no es un efecto
 * secundario, es la decisión.
 *
 * El camino de Firestore NO se toca: sigue promediando su página, exactamente
 * como hasta hoy.
 */

/** 20 por página: el mismo `limit(20)` que la pantalla ya pedía. */
export const REVIEWS_PAGE_SIZE = 20;

/** Una reseña con los nombres que la pantalla ya pinta. */
export function adaptReview(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id,
    clientId: row.clientId ?? null,
    clientName: row.clientName ?? '',
    rating: Number(row.rating) || 0,
    comment: row.comment ?? null,
    // Texto ISO. `ProductDetail` ya sabe leer las dos formas (`Timestamp` de
    // Firestore con `.toDate()` o algo que `new Date()` entienda), así que el
    // JSX de la lista no cambia por este lado.
    createdAt: row.createdAt ?? null,
  };
}

/**
 * La respuesta del endpoint convertida en lo que la pantalla necesita.
 *
 * `average` puede ser `null` legítimamente: un producto sin ninguna reseña no
 * tiene nota, y un `0` ahí pinta cinco estrellas vacías, que es una afirmación
 * distinta ("malísimo") de "todavía nadie".
 */
export function adaptReviewPage(payload, { page = 1, pageSize = REVIEWS_PAGE_SIZE } = {}) {
  const lista = payload && (payload.items ?? payload.reviews);
  if (!Array.isArray(lista)) {
    throw new ApiError('El API no devolvió una lista de reseñas.', { code: 'bad_response' });
  }

  const crudoTotal = payload.total ?? payload.totalCount;
  const total = Number.isFinite(Number(crudoTotal)) && crudoTotal !== null && crudoTotal !== undefined
    ? Number(crudoTotal)
    : null;

  // 🔴 NO se cae al promedio de la página si el servidor no lo manda. Ese
  // respaldo sería justo la mentira que este endpoint viene a quitar, y encima
  // silenciosa. Sin promedio del servidor, no hay promedio.
  const crudoAvg = payload.average;
  const average = crudoAvg === null || crudoAvg === undefined || !Number.isFinite(Number(crudoAvg))
    ? null
    : Number(crudoAvg);

  const tam = Number(payload.pageSize) || pageSize;
  const paginasDelServidor = Number(payload.totalPages);

  return {
    reviews: lista.map(adaptReview).filter(Boolean),
    total,
    average,
    // ¿QUIEN PREGUNTA ya reseñó este producto? Es un dato sobre uno mismo, no
    // sobre la lista, y contesta de una sola vez lo que si no habría que buscar
    // recorriendo páginas (y que fallaría en silencio en cuanto el cliente
    // cayera fuera de la primera). `false` cuando no se sabe: el peor caso es
    // ofrecer un botón que el servidor rechazará por duplicado, y el contrario
    // sería esconderle el botón a quien nunca ha reseñado nada.
    viewerHasReviewed: payload.viewerHasReviewed === true,
    totalPages: Number.isFinite(paginasDelServidor) && paginasDelServidor > 0
      ? paginasDelServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload.page) || page,
    pageSize: tam,
  };
}

/**
 * Una página de reseñas de un producto.
 *
 * ⚠️ EL 404 NO ES UN ERROR, ES UNA RESPUESTA — y aquí por partida doble: el API
 * responde 404 cuando el producto no existe Y cuando no es visible, igual que la
 * ficha y a propósito (distinguirlos diría cuáles están restringidos). Si el
 * producto no se puede ver, la ficha ya lo está diciendo; pintar además "no se
 * pudo cargar" en las reseñas sería un segundo error inventado. Cualquier OTRO
 * fallo SÍ sube: una red caída no puede parecer un producto sin reseñas.
 */
export async function fetchProductReviews(id, {
  page = 1,
  pageSize = REVIEWS_PAGE_SIZE,
  apiFetchImpl,
  signal,
} = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = { page, pageSize };
  try {
    const payload = await call(`/products/${encodeURIComponent(id)}/reviews`, { query, signal });
    return adaptReviewPage(payload, { page, pageSize });
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

/**
 * ─── LA ESCRITURA DEL CATÁLOGO ──────────────────────────────────────────────
 *
 * Vive AQUÍ, junto a las lecturas, y no dentro de `WorkshopProducts.js`. Es la
 * misma razón por la que existe este archivo: cuatro copias del mismo lector
 * hubo que unificar esta semana, y una quinta —esta vez de un ESCRITOR— sería
 * exactamente la deuda que la migración está pagando.
 *
 * 🔴 LAS TRES TRAMPAS DE ESTA TRADUCCIÓN, POR ORDEN DE LO QUE CUESTAN:
 *
 *  1. Los esquemas del API son `.strict()`. Un campo de más NO se ignora: es un
 *     400 con su nombre. El objeto que la pantalla arma para Firestore lleva
 *     `workshopName`, `compatibleMakes`, `inStock`, `active` y `updatedAt`, y
 *     ninguno puede viajar. Por eso el cuerpo se construye campo a campo (lista
 *     de PERMITIDOS) y jamás con un `...data`: un `...` es una lista de
 *     prohibidos disfrazada, y falla al AÑADIR un campo.
 *  2. Los precios van en CENTAVOS ENTEROS (`z.number().int()`).
 *  3. `variationId` es `id` en Firestore. Es la clave de los carritos vivos en
 *     `localStorage` y de las órdenes históricas: se traduce el NOMBRE y se
 *     conserva el VALOR tal cual.
 *
 * Y la separación que no se toca: la ficha (`PATCH /products/:id`) NO acepta
 * `active` ni `stock`. Publicar es otra ruta a propósito, porque mezclarlas es
 * lo que hacía que guardar un precio republicara un producto retirado.
 */

/** El mismo tope que `MAX_PRODUCT_PHOTOS` en api/src/services/catalog.ts. */
export const MAX_PRODUCT_PHOTOS = 3;

/** Lo que se rechaza ANTES de salir a la red. Lleva `code` para poder traducirlo. */
export class ProductInputError extends Error {
  constructor(message, code = 'invalid_input') {
    super(message);
    this.name = 'ProductInputError';
    this.code = code;
  }
}

/** 'car' → 'CAR'. El enum de la base va en mayúsculas. */
const VEHICLE_CATEGORY_API = { car: 'CAR', moto: 'MOTO', both: 'BOTH' };

/** Texto con contenido, o `null`. `''` no es un valor, es un campo vacío. */
function nulable(v) {
  const s = typeof v === 'string' ? v.trim() : (v === 0 ? '0' : '');
  return s === '' ? null : s;
}

/** Pesos → centavos enteros. Devuelve `null` si no hay número que convertir. */
function centavos(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function entero(v) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * El cuerpo que espera el API, a partir del objeto que la pantalla ya arma.
 *
 * `create` añade `workshopId` (obligatorio al nacer, inmutable después) y el
 * inventario INICIAL. En la edición ninguno de los dos viaja: `stock` se repone
 * con un ajuste por diferencia, nunca con el valor absoluto de un formulario que
 * pudo cargarse hace minutos y llevar unidades ya vendidas.
 *
 * `existingVariationIds` son las variaciones que YA existían cuando se abrió el
 * formulario. A esas no se les manda `stock` (el API responde 400 nombrándolas);
 * a las que nacieron en esta sesión, sí.
 */
export function productApiBody(data = {}, { create = false, existingVariationIds = [] } = {}) {
  const universal = data.isUniversal === true;
  const conVariaciones = data.hasVariations === true;
  const yaExistia = new Set((existingVariationIds || []).map((v) => String(v)));

  const fotos = Array.isArray(data.photos) ? data.photos.filter(Boolean) : [];
  if (fotos.length > MAX_PRODUCT_PHOTOS) {
    // No se sale a la red a que nos digan que no: el viaje ya está pagado en
    // subidas a Cloudinary que el taller haría para nada.
    throw new ProductInputError(
      `photos: como máximo ${MAX_PRODUCT_PHOTOS} fotos por producto.`,
      'too_many_photos',
    );
  }

  const body = {
    name: typeof data.name === 'string' ? data.name.trim() : '',
    description: nulable(data.description),
    category: nulable(data.category),
    // ⚠️ La MISMA marca con dos nombres: `brand` en Firestore, `partBrand` aquí.
    partBrand: nulable(data.brand ?? data.partBrand),
    partNumber: nulable(data.partNumber),
    priceCents: centavos(data.price),
    comparePriceCents: centavos(data.comparePrice),
    sellsToDistributors: data.sellsToDistributors === true,
    videoUrl: nulable(data.videoURL ?? data.videoUrl),
    vehicleCategory: VEHICLE_CATEGORY_API[String(data.vehicleCategory || 'car').toLowerCase()] || 'CAR',
    isUniversal: universal,
    hasVariations: conVariaciones,
    variationLabel: conVariaciones ? nulable(data.variationLabel) : null,
    deliveryAvailable: data.deliveryAvailable === true,
    pickupAvailable: data.pickupAvailable === true,
    installationAvailable: data.installationAvailable === true,
    installationPriceCents: data.installationAvailable === true ? centavos(data.installationPrice) : null,
    photos: fotos,
    variations: conVariaciones
      ? (data.variations || []).map((v) => {
          const id = String(v.id ?? v.variationId ?? '');
          const fila = { variationId: id, name: (v.name || '').trim(), priceCents: centavos(v.price) };
          if (!yaExistia.has(id)) fila.stock = entero(v.stock);
          return fila;
        })
      // Con la lista vacía se retiran las que hubiera: apagar el interruptor de
      // variaciones tiene que llegar a la base, no quedarse en la pantalla.
      : [],
    compatibility: universal
      ? []
      : (data.compatibleVehicles || []).map((c) => ({
          make: c.make,
          // 🔴 NULL = SIN LÍMITE. Un '' o un 0 aquí vacían el filtro del catálogo
          // sin dar un solo error (COMPATIBILITY_SQL usa `IS NULL OR ...`).
          model: c.model ?? null,
          yearFrom: c.yearFrom ?? null,
          yearTo: c.yearTo ?? null,
        })),
  };

  // 🔴 `supplierPriceCents` NO SE MANDA NUNCA, y omitirlo es la decisión.
  //
  // La cifra de distribuidor se retiró de la app el 2026-08-12: la pantalla ya no
  // la pide, así que mandar `null` "porque no hay input" BORRARÍA el número que
  // el taller guardó antes. Omitir el campo lo deja intacto en su columna, listo
  // para el módulo mayorista de verdad. La columna y su ruta de lectura del
  // propio dueño siguen vivas en el API a propósito.
  if (create) {
    body.workshopId = data.workshopId;
    body.stock = entero(data.stock);
  }
  return body;
}

/** Crea el producto. El acuse trae `id`; NINGÚN importe, por construcción. */
export async function createProduct(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/products', { method: 'POST', body, signal });
}

/** La FICHA. No mueve publicación ni inventario: para eso están las otras rutas. */
export async function updateProduct(id, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/products/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal });
}

/** Publicar / despublicar. RUTA APARTE, y esa separación es la que protege. */
export async function setProductPublication(id, active, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/products/${encodeURIComponent(id)}/publication`, {
    method: 'PATCH',
    body: { active: active === true },
    signal,
  });
}

/**
 * ─── LA COLA DE MODERACIÓN DEL ADMIN ────────────────────────────────────────
 *
 * 🔴 POR QUÉ NO SIRVE `fetchCatalogPage` PARA ESTA PANTALLA. `GET /products` es
 * la VITRINA, y la vitrina esconde por construcción lo despublicado y lo
 * sancionado — que es exactamente lo que el panel viene a moderar. Reusarla
 * dejaría las pestañas "Ocultos", "Restringidos" y "Apelaciones" vacías, y no
 * vacías con un error: vacías y pareciendo limpias.
 *
 * El endpoint es `GET /admin/products` y es SOLO de admin (403 antes de tocar la
 * base, no una lista vacía haciendo de "no puedes").
 */

/** El mismo tamaño que el resto del catálogo. */
export const ADMIN_PRODUCTS_PAGE_SIZE = 50;

/**
 * Una fila del API con la forma que `AdminProductsMod` ya pinta.
 *
 * Se traduce al vocabulario del documento de Firestore (`price`, `photos[]`,
 * `restrictionAppeal`) A PROPÓSITO: la pantalla tiene que renderizar igual venga
 * de donde venga, y el JSX es lo único que no debe bifurcarse. Lo que NO se
 * inventa es lo que el API no manda.
 */
export function adaptAdminProduct(row) {
  if (!row || typeof row !== 'object') return null;
  const apelacion = row.appeal && typeof row.appeal === 'object' ? row.appeal : null;
  return {
    id: row.id,
    name: row.name ?? '',
    workshopId: row.workshopId ?? null,
    workshopName: row.workshopName ?? '',
    price: money(row.priceCents, row.price),
    stock: row.stock === null || row.stock === undefined ? null : Number(row.stock),
    // `active` viaja SIEMPRE explícito desde Postgres. En Firestore el ausente
    // significaba activo; aquí no hay ausentes, así que no se emula el tri-estado.
    active: row.active !== false,
    adminRestricted: row.adminRestricted === true,
    adminRestrictionReason: row.adminRestrictionReason ?? null,
    // La fila del panel solo enseña la primera. El API manda esa y nada más:
    // traerse las seis fotos de 500 productos para pintar un cuadradito de 56px
    // es lo que hace lenta una cola de moderación.
    photos: row.photo ? [row.photo] : [],
    // El panel lee `restrictionAppeal?.status === 'pending'` en minúsculas; el
    // API habla en mayúsculas (`AppealStatus`). Se traduce aquí y no en el JSX.
    restrictionAppeal: apelacion
      ? {
          text: apelacion.text ?? '',
          status: String(apelacion.status ?? '').toLowerCase(),
          submittedAt: apelacion.submittedAt ?? null,
        }
      : null,
  };
}

/**
 * La página del panel.
 *
 * `pendingAppeals` es el total de TODO el catálogo, no el de esta página: la
 * pestaña lleva un contador y sobre la página diría "(0)" habiendo apelaciones
 * en la página 2, que es justo el aviso que existe para que alguien entre.
 */
export function adaptAdminProductPage(payload, { page = 1, pageSize = ADMIN_PRODUCTS_PAGE_SIZE } = {}) {
  const lista = payload && payload.items;
  if (!Array.isArray(lista)) {
    throw new ApiError('El API no devolvió una lista de productos.', { code: 'bad_response' });
  }
  const tam = Number(payload.pageSize) || pageSize;
  const total = Number.isFinite(Number(payload.total)) ? Number(payload.total) : null;
  const paginasDelServidor = Number(payload.totalPages);
  return {
    products: lista.map(adaptAdminProduct).filter(Boolean),
    total,
    totalPages: Number.isFinite(paginasDelServidor) && paginasDelServidor > 0
      ? paginasDelServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload.page) || page,
    pageSize: tam,
    pendingAppeals: Number(payload.pendingAppeals) || 0,
  };
}

/** Una página de la cola de moderación. Los errores SUBEN (`LoadFailed`). */
export async function fetchAdminProducts(
  { page = 1, pageSize = ADMIN_PRODUCTS_PAGE_SIZE, status = 'all', q } = {},
  { apiFetchImpl, signal } = {},
) {
  const call = apiFetchImpl || apiFetch;
  const query = { page, pageSize, status };
  if (q) query.q = q;
  const payload = await call('/admin/products', { query, signal });
  return adaptAdminProductPage(payload, { page, pageSize });
}

/**
 * RESTRINGIR / LEVANTAR. Ruta aparte de la ficha, y esa separación es la que
 * impide que el vendedor se levante su propia sanción guardando el formulario.
 *
 * Al restringir, el motivo es OBLIGATORIO del lado del servidor: sin él el dueño
 * no sabe qué apelar. Al levantar no se manda ninguno (el API lo rechazaría).
 */
export async function setProductRestriction(id, restricted, reason, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = { restricted: restricted === true };
  if (restricted === true && reason) body.reason = reason;
  return call(`/products/${encodeURIComponent(id)}/restriction`, { method: 'PATCH', body, signal });
}

/**
 * RECHAZAR la apelación.
 *
 * ⚠️ NO existe una ruta para APROBARLA, y no es un hueco: aprobar una apelación
 * ES levantar la sanción, y `setProductRestriction(id, false)` cierra la
 * apelación pendiente en la misma transacción. Dos caminos para lo mismo es cómo
 * se acaba con un producto restituido y una apelación todavía en la cola.
 */
export async function rejectProductAppeal(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/products/${encodeURIComponent(id)}/appeal`, { method: 'PATCH', signal });
}

/**
 * RETIRAR el producto. En Postgres es `deleted_at`, no un borrado de verdad, y
 * la diferencia importa: `product_review` cuelga con `ON DELETE CASCADE`, así
 * que un borrado real le daría a cualquier vendedor la forma más limpia de hacer
 * desaparecer una reseña de una estrella.
 */
export async function deleteProduct(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/products/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
}

/** Cuánto del motivo cabe en un aviso sin volverse ilegible. */
const MAX_MOTIVO = 240;

/**
 * EL MOTIVO REAL DEL RECHAZO, para enseñárselo al taller.
 *
 * 🔴 El API contesta 400 NOMBRANDO el campo que no acepta. Tragarse eso y decir
 * "Error al guardar" deja al taller adivinando; tragárselo y decir "Producto
 * actualizado" es peor, y tiene antecedentes en este proyecto: pantallas que
 * celebraban un guardado mientras las reglas lo denegaban en silencio.
 *
 * Solo se le enseña al usuario lo que viene del API (`ApiError`, incluidos los
 * fallos de red, que también le dicen algo útil) o lo que rechazamos nosotros a
 * conciencia (`ProductInputError`). Un `TypeError` nuestro NO: eso es un fallo
 * de programación, y disfrazarlo de respuesta del servidor manda al taller a
 * revisar un formulario que estaba bien.
 */
export function apiErrorReason(err) {
  if (!err || typeof err.message !== 'string') return null;
  if (err.name !== 'ApiError' && err.name !== 'ProductInputError') return null;
  const msg = err.message.trim();
  if (!msg) return null;
  return msg.length > MAX_MOTIVO ? `${msg.slice(0, MAX_MOTIVO - 1)}…` : msg;
}

/**
 * ─── EL INVENTARIO DEL PROPIO TALLER ────────────────────────────────────────
 *
 * 🔴 POR QUÉ NO SIRVE `fetchCatalogPage` PARA LA PANTALLA DEL VENDEDOR, que es
 * la misma razón por la que el admin tiene la suya: `GET /products` es la
 * VITRINA y esconde por construcción lo despublicado, lo agotado y lo
 * sancionado, o sea EXACTAMENTE lo que el taller entra a arreglar. Reusarla
 * dejaría al vendedor sin ver el producto que pausó, sin ver el que se le acabó
 * y sin ver el que le restringieron; y no vacío con un error, sino vacío
 * pareciendo que nunca existió.
 *
 * El endpoint es `GET /workshops/:id/products` y es el ÚNICO sitio del API por
 * donde viaja `supplier_price_cents`. Eso no es un detalle: el precio de
 * distribuidor es el margen del negocio, se selecciona aquí porque su dueño
 * tiene que poder editarlo, y no se selecciona en ninguna consulta de la
 * vitrina. Las dos mitades están vigiladas por pruebas a los dos lados.
 */

/** El mismo tamaño que el resto del catálogo. */
export const INVENTORY_PAGE_SIZE = 50;

/**
 * Una fila del inventario con el vocabulario del documento de Firestore.
 *
 * 🔴 LAS TRES LISTAS HIJAS SE CONSERVAN ENTERAS. `PATCH /products/:id` REEMPLAZA
 * fotos, variaciones y compatibilidad de golpe: si el formulario se abriera sin
 * ellas y el vendedor guardara un precio, las borraría. Por eso el API las manda
 * completas y por eso aquí no se recortan.
 */
export function adaptInventoryProduct(row) {
  if (!row || typeof row !== 'object') return null;
  const base = adaptProduct(row);
  if (!base) return null;
  // La apelación viene con la MISMA forma que en la cola del admin, así que se
  // traduce igual: el API habla en mayúsculas (`AppealStatus`) y la pantalla lee
  // `restrictionAppeal?.status === 'pending'` en minúsculas.
  //
  // 🔴 SIN ESTE CAMPO EL VENDEDOR VUELVE A VER EL BOTÓN DE APELAR aunque ya haya
  // apelado, lo pulsa, y se lleva el 409 de "ya hay una pendiente": el candado
  // del servidor funcionando y la pantalla sin enterarse.
  const apelacion = row.appeal && typeof row.appeal === 'object' ? row.appeal : null;
  return {
    ...base,
    restrictionAppeal: apelacion
      ? {
          text: apelacion.text ?? '',
          status: String(apelacion.status ?? '').toLowerCase(),
          submittedAt: apelacion.submittedAt ?? null,
        }
      : null,
    // El de la vitrina llega ausente por construcción; el del inventario llega
    // de verdad. `adaptProduct` ya lo pasa por el mismo conversor, así que aquí
    // solo se deja dicho que en ESTA ruta sí existe.
    supplierPrice: base.supplierPrice,
    // El porqué de la sanción, que es lo que el taller apela. El API solo lo
    // manda mientras la sanción esté puesta.
    adminRestrictionReason: row.adminRestrictionReason ?? null,
    updatedAt: row.updatedAt ?? null,
  };
}

/** Una página del inventario. Los errores SUBEN (`LoadFailed`, no lista vacía). */
export async function fetchWorkshopInventory(workshopId, {
  page = 1,
  pageSize = INVENTORY_PAGE_SIZE,
  q,
  status = 'all',
} = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = { page, pageSize, status };
  if (q) query.q = q;
  const payload = await call(`/workshops/${encodeURIComponent(workshopId)}/products`, { query, signal });
  const lista = payload && payload.items;
  if (!Array.isArray(lista)) {
    throw new ApiError('El API no devolvió una lista de productos.', { code: 'bad_response' });
  }
  const tam = Number(payload.pageSize) || pageSize;
  const total = Number.isFinite(Number(payload.total)) ? Number(payload.total) : null;
  const paginasDelServidor = Number(payload.totalPages);
  return {
    products: lista.map(adaptInventoryProduct).filter(Boolean),
    total,
    totalPages: Number.isFinite(paginasDelServidor) && paginasDelServidor > 0
      ? paginasDelServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload.page) || page,
    pageSize: tam,
  };
}

/**
 * PRESENTAR la apelación. Es del DUEÑO del producto sancionado.
 *
 * ⚠️ Verbo distinto al del rechazo a propósito: `POST` la PRESENTA (el dueño),
 * `PATCH` la RECHAZA (el admin). Compartir verbo obligaría a un cuerpo que
 * distinga quién habla, y quién habla ya lo dice el token; mezclar las dos
 * decisiones en un campo es cómo se acaba rechazando una apelación al intentar
 * presentarla.
 */
export async function submitProductAppeal(id, text, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const motivo = typeof text === 'string' ? text.trim() : '';
  if (!motivo) {
    // No se gasta el viaje: el API contestaría justo esto y el mensaje no sería
    // más claro que el de aquí.
    throw new ProductInputError('text: hay que decir por qué se apela.', 'no_text');
  }
  return call(`/products/${encodeURIComponent(id)}/appeal`, {
    method: 'POST',
    body: { text: motivo },
    signal,
  });
}

/**
 * DEJAR UNA RESEÑA del producto.
 *
 * 🔴 EL COMPRADOR NO MANDA SU NOMBRE NI LA FECHA. Hoy el camino de Firestore
 * escribe `clientName` y `createdAt` desde el navegador, o sea que el reloj del
 * aparato y el nombre que él diga acaban dentro de una reseña pública. El API
 * los resuelve del token y del servidor: se manda la nota y el texto, nada más.
 * Mandar de más no se ignora, el esquema es estricto y responde 400 con el
 * nombre del campo que sobra.
 */
export async function createProductReview(id, { rating, comment } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const nota = Math.round(Number(rating));
  if (!Number.isFinite(nota) || nota < 1 || nota > 5) {
    throw new ProductInputError('rating: la nota va de 1 a 5.', 'bad_rating');
  }
  const body = { rating: nota };
  const texto = typeof comment === 'string' ? comment.trim() : '';
  // Se OMITE cuando no hay: una reseña sin comentario es legítima, pero un `''`
  // guardado es un comentario vacío que la pantalla pinta como una línea en
  // blanco bajo las estrellas.
  if (texto) body.comment = texto;
  return call(`/products/${encodeURIComponent(id)}/reviews`, { method: 'POST', body, signal });
}

/**
 * CONTAR LA VISITA.
 *
 * Es lo único de la ficha que NO puede fallar hacia arriba: un contador de
 * visitas que tumba la pantalla del producto cambia un dato de analítica por una
 * venta. Por eso devuelve si pudo y se traga el fallo, igual que el
 * `.catch(() => {})` que el camino de Firestore lleva desde siempre.
 */
export async function countProductView(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    await call(`/products/${encodeURIComponent(id)}/view`, { method: 'POST', body: {}, signal });
    return true;
  } catch {
    return false;
  }
}
